import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, X, Briefcase, Calendar, ArrowRight, Award, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { professionalData } from './professionalData';
import '../App.css';

const LOGO_FOLDER = encodeURIComponent('logos, pe');

const getLogoSrc = (logo) =>
  logo ? `/Projects/${LOGO_FOLDER}/${encodeURIComponent(logo)}` : null;

const getCompanyName = (companyAndRole = '') =>
  companyAndRole.split('\n')[0].split('—')[0].trim();

const getRoleLine = (companyAndRole = '') =>
  companyAndRole.includes('\n')
    ? companyAndRole.split('\n')[1]
    : companyAndRole.split('—')[1]?.trim();

const WorksProfessionalPage = ({ nightMode, setNightMode }) => {
  const navigate = useNavigate();
  const [selectedCompany, setSelectedCompany] = useState(null);

  const closeDetail = () => setSelectedCompany(null);

  return (
    <div className={`works-professional-page-new ${nightMode ? 'night' : ''}`}>
      {/* Navigation */}
      <div className="works-nav">
        <motion.button
          className="works-back"
          onClick={() => navigate('/works')}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -6 }}
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </motion.button>

        <motion.button
          className="works-theme"
          onClick={() => setNightMode(!nightMode)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {nightMode ? '☀' : '🌙'}
        </motion.button>
      </div>

      {/* Hero Section */}
      <div className="projects-hero">
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <span>PROFESSIONAL</span>
          <span>EXPERIENCE</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          From the precision of factories to the reach of export houses, the pulse of retail, and the pace of e-commerce, my journey across the industry's full spectrum.
        </motion.p>
      </div>

      {/* Company Cards Grid */}
      <motion.div
        className="professional-grid"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {professionalData.map((company, index) => (
          <motion.button
            key={company.slug}
            className="professional-card"
            onClick={() => setSelectedCompany(company)}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 * index }}
            whileHover={{ y: -8, scale: 1.02 }}
            style={{
              '--card-accent': company.accent,
              '--card-accent-secondary': company.accentSecondary,
            }}
          >
            {/* Company Image Placeholder */}
            <div className="professional-card__image">
              <div className="professional-card__image-circle">
                {company.logo ? (
                  <img
                    src={getLogoSrc(company.logo)}
                    alt={`${getCompanyName(company.companyAndRole)} logo`}
                  />
                ) : (
                  <Briefcase size={40} strokeWidth={1.5} />
                )}
              </div>
            </div>

            {/* Card Content */}
            <div className="professional-card__content">
              <div className="professional-card__header">
                <span className="professional-card__timeline">
                  <Calendar size={12} />
                  {company.timeline}
                </span>

                <h3 className="professional-card__company">{getCompanyName(company.companyAndRole)}</h3>

                <p className="professional-card__role">{getRoleLine(company.companyAndRole)}</p>
              </div>
            </div>
          </motion.button>
        ))}
      </motion.div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedCompany && (
          <motion.div
            className="professional-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeDetail}
          >
            <motion.div
              className="professional-modal__content"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 400 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                '--modal-accent': selectedCompany.accent,
                '--modal-accent-secondary': selectedCompany.accentSecondary,
              }}
            >
              {/* Close Button */}
              <motion.button
                className="professional-modal__close"
                onClick={closeDetail}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <X size={24} />
              </motion.button>

              {/* Modal Header */}
              <div className="professional-modal__header">
                <div className="professional-modal__image-circle">
                  {selectedCompany.logo ? (
                    <img
                      src={getLogoSrc(selectedCompany.logo)}
                      alt={`${getCompanyName(selectedCompany.companyAndRole)} logo`}
                    />
                  ) : (
                    <Briefcase size={40} strokeWidth={1.5} />
                  )}
                </div>
                <div className="professional-modal__title-block">
                  <span className="professional-modal__timeline">
                    <Calendar size={12} />
                    {selectedCompany.timeline}
                  </span>

                  <h2 className="professional-modal__company">
                    {getCompanyName(selectedCompany.companyAndRole)}
                  </h2>

                  <p className="professional-modal__role">{getRoleLine(selectedCompany.companyAndRole)}</p>
                </div>
              </div>

              {/* Modal Body */}
              <div className="professional-modal__body">
                {/* Description */}
                <section className="professional-modal__section">
                  <h3 className="professional-modal__section-title">About</h3>
                  <div className="professional-modal__description">
                    {selectedCompany.description.split('\n').map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                </section>

                {/* Key Achievements */}
                {selectedCompany.highlights && (
                  <section className="professional-modal__section">
                    <h3 className="professional-modal__section-title">
                      <Award size={20} />
                      Key Achievements
                    </h3>
                    <ul className="professional-modal__list">
                      {selectedCompany.highlights.map((item, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 * i }}
                        >
                          {item}
                        </motion.li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Skills Grid */}
                <div className="professional-modal__skills-grid">
                  {/* Skills Gained */}
                  {selectedCompany.skillsGained && (
                    <section className="professional-modal__section">
                      <h3 className="professional-modal__section-title">Skills Gained</h3>
                      <div className="professional-modal__chips">
                        {selectedCompany.skillsGained.map((skill, i) => (
                          <motion.span
                            key={i}
                            className="professional-modal__chip"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.05 * i }}
                          >
                            {skill}
                          </motion.span>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Soft Skills */}
                  {selectedCompany.softSkills && (
                    <section className="professional-modal__section">
                      <h3 className="professional-modal__section-title">Soft Skills</h3>
                      <div className="professional-modal__chips">
                        {selectedCompany.softSkills.map((skill, i) => (
                          <motion.span
                            key={i}
                            className="professional-modal__chip professional-modal__chip--soft"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.05 * i }}
                          >
                            {skill}
                          </motion.span>
                        ))}
                      </div>
                    </section>
                  )}
                </div>

                {/* Tools & Technologies */}
                {selectedCompany.tools && (
                  <section className="professional-modal__section">
                    <h3 className="professional-modal__section-title">
                      <Wrench size={20} />
                      Tools & Technologies
                    </h3>
                    <div className="professional-modal__tools">
                      {selectedCompany.tools.map((tool, i) => (
                        <motion.div
                          key={i}
                          className="professional-modal__tool"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.05 * i }}
                        >
                          {tool}
                        </motion.div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Navigation activeSection="work" onNavigate={() => {}} nightMode={nightMode} />
    </div>
  );
};

export default WorksProfessionalPage;
